// @ts-check
import { buffers, eventChannel } from 'redux-saga';
import { call, put, take } from 'redux-saga/effects';

import { updateInfo } from '../actions';
import { BREAKPOINTS } from '../constants';

/**
 * Given pixel size for min and max breakpoint size, returns mediaQueryString
 * @param {number | undefined} minWidth
 * @param {number | undefined} maxWidth
 * @return {string} mediaQueryString
 */
const getMediaQueryString = (minWidth, maxWidth) =>
	[minWidth && `(min-width: ${minWidth}px)`, maxWidth && `(max-width: ${maxWidth}px)`]
		.filter(Boolean)
		.join(' and ');

/**
 * Given list of pixel breakpoints returns redux-saga eventChannel
 * (observable-like object) producing low breakpoint for currently matching
 * breakpoint range.
 * @param {number[]} breakpoints
 * @return {import('redux-saga').EventChannel<number>}
 */
const createScreenResizeChannel = (breakpoints) =>
	eventChannel((emitter) => {
		const sortedBreakpoints = breakpoints.sort((a, b) => a - b);
		const breakpointHandlers = sortedBreakpoints.map((breakpoint, idx) => {
			const nextBreakpoint = sortedBreakpoints[idx + 1];
			const media = getMediaQueryString(breakpoint, nextBreakpoint && nextBreakpoint - 1);
			const mediaQueryList = window.matchMedia(media);
			const listener = (event) => {
				if (event.matches) emitter(breakpoint);
			};
			mediaQueryList.addListener(listener);
			if (mediaQueryList.matches) emitter(breakpoint);

			return { mediaQueryList, listener };
		});

		return () => {
			// unsubscribe (cleanup) function
			breakpointHandlers.forEach(({ mediaQueryList, listener }) => {
				mediaQueryList.removeListener(listener);
			});
		};
	}, buffers.sliding(1));

export default function* bootstrap() {
	const screenResizeChannel = yield call(
		createScreenResizeChannel,
		Object.keys(BREAKPOINTS).map(Number)
	);

	while (true) {
		const breakpoint = yield take(screenResizeChannel);
		yield put(updateInfo({ screenSize: BREAKPOINTS[breakpoint] }));
	}
}
